import { getGdriveConnectionIneligibleReason } from '../../../banners/utils/connectorEligibility.ts';
import type { TopApp } from '../../../utils/unconnectedThirdPartyApps.ts';
import type { ConnectorPromoBannerSelection } from '../connectorPromoBannerModel.ts';

export type SelectConnectorPromoBannerInput = {
	isPageFirstPartyOnly: boolean;
	hasSiteWideDriveLinks: boolean;
	isGdriveConnected: boolean;
	hasUnconnectedThirdParty: boolean;
	topApp: TopApp | undefined;
};

/**
 * Picks the banner variant, first match winning:
 *
 * 1. **Google Drive** — 1P-only page, the org already links to Drive, this user has not
 *    connected it.
 * 2. **Org connected apps** — an unauthorized 3P link whose app the org already uses.
 * 3. **Generic** — neither.
 *
 * A losing variant's first failed check rides along on the `viewed` event, so a generic
 * banner still says why the other two were ruled out.
 */
export const selectConnectorPromoBanner = ({
	isPageFirstPartyOnly,
	hasSiteWideDriveLinks,
	isGdriveConnected,
	hasUnconnectedThirdParty,
	topApp,
}: SelectConnectorPromoBannerInput): ConnectorPromoBannerSelection => {
	// `undefined` means Drive qualified, so the variant decision and its reason cannot drift.
	const gdriveIneligibleReason = isPageFirstPartyOnly
		? getGdriveConnectionIneligibleReason({
				hasSiteWideDriveLinks,
				isAuthenticated: isGdriveConnected,
			})
		: 'notFirstPartyOnly';

	if (gdriveIneligibleReason === undefined) {
		return { variant: 'gdrive' };
	}
	if (hasUnconnectedThirdParty && topApp) {
		return { variant: 'orgConnectedApps', topApp, gdriveIneligibleReason };
	}
	return {
		variant: 'generic',
		gdriveIneligibleReason,
		orgIneligibleReason: hasUnconnectedThirdParty ? 'noTopApp' : 'noUnconnectedThirdParty',
	};
};
